// Auf dem Foto zeichnen (Mockup /doku/zeichnen/:nr/:i): ein eigener,
// dunkler Bildschirm, das Foto so groß wie möglich, darunter die Werkzeuge
// – Stift (frei), Kreis (aufziehen), Pfeil (von – bis), Zurück (den
// letzten Strich weg) und Fertig. In Lime wie im Mockup; mit dem Finger am
// iPhone, mit dem Stift am Surface, mit der Maus am Laptop (Pointer Events).
// Zeichnet einmal ein Stift, zählt der Finger auf diesem Bildschirm nicht
// mehr – so malt der aufgelegte Handballen nicht mit (handballen.ts, seit
// 19.10.2026).
//
// Seit 22.10.2026 (O66) sind die Striche dünner. Seit E3b (Gernot,
// 22.10.2026 nach dem Nachtest am iPhone) über den Werkzeugen die Wahl:
// drei Stärken – „dünn“ ganz dezent und vorgewählt, „mittel“, „dick“ – und
// drei Farben – Lime, Türkis, Pink (Claudes Wahl, O81 e). Die Wahl merkt
// sich das Gerät; jeder neue Strich trägt sie. Ältere Striche bleiben, wie
// sie sind („mittel“, Lime).
//
// Gezeichnet wird in Daten, nicht in Pixeln: Jeder Strich ist eine Folge von
// Punkten zwischen 0 und 1 (aufnahme-vertrag.ts). Das Bild darüber kommt
// aus dem Kern (zeichnungSvgInhalt) – genau so, wie es später im Büro
// aussieht. Das Foto selbst bleibt unverändert.
//
// Gespeichert wird von selbst, kurz nach jedem Strich und spätestens beim
// Verlassen – auch wenn die App mittendrin geschlossen wird. Ist die
// Besichtigung gesendet (seit Schritt 4), steht hier nur ein Hinweis – außer
// auf einem Foto, das mit dem laufenden Nachtrag dazukam (seit Schritt 5).
//
// Adresse: #/aufnahme/<kennung>/station/<nr>/foto/<foto-kennung>/zeichnen

import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  AUFNAHME_GRENZEN,
  STRICH_FARBWERTE,
  type IchAntwort,
  type StrichFarbe,
  type StrichStaerke,
  type Zeichnung,
  type ZeichenWerkzeug,
} from "@happy-energy/kern";
import { AppKopf, Ikon, Tipp, toastZeigen, type IkonName } from "@happy-energy/oberflaeche";
import { zeichnungSpeichern } from "../arbeitskopie.ts";
import { Handballensperre } from "../handballen.ts";
import type { Foto } from "../speicher.ts";
import { useSpeichern } from "./gemeinsam.tsx";
import { seitenverhaeltnis, useDateiAdresse, ZeichenEbene } from "./fotos.tsx";
import { fotoAdresse, fotoBearbeitbar, FotoFehlt, useFotoDaten } from "./FotoSeite.tsx";
import { modusVon } from "./Uebersicht.tsx";

type Strich = Zeichnung["striche"][number];

const WERKZEUGE: { wert: ZeichenWerkzeug; name: string; ikon: IkonName; tipp: string }[] = [
  { wert: "stift", name: "Stift", ikon: "stift", tipp: "Mit Finger oder Stift zeichnen" },
  { wert: "kreis", name: "Kreis", ikon: "kreis", tipp: "Kreis aufziehen" },
  { wert: "pfeil", name: "Pfeil", ikon: "pfeil", tipp: "Pfeil ziehen: von – bis" },
];

const STAERKEN: { wert: StrichStaerke; name: string }[] = [
  { wert: "duenn", name: "dünn" },
  { wert: "mittel", name: "mittel" },
  { wert: "dick", name: "dick" },
];

const FARBEN: { wert: StrichFarbe; name: string }[] = [
  { wert: "lime", name: "Lime" },
  { wert: "tuerkis", name: "Türkis" },
  { wert: "pink", name: "Pink" },
];

/** Was das Gerät sich merkt: zuletzt gewählte Stärke und Farbe. */
const WAHL_SCHLUESSEL = "he-doku-zeichnen";

/** Die gemerkte Wahl – ohne (oder kaputt, privater Modus): dünn und Lime. */
export function wahlLesen(roh: string | null): { staerke: StrichStaerke; farbe: StrichFarbe } {
  const vorgabe = { staerke: "duenn" as StrichStaerke, farbe: "lime" as StrichFarbe };
  if (!roh) return vorgabe;
  try {
    const w = JSON.parse(roh) as { staerke?: unknown; farbe?: unknown };
    return {
      staerke: STAERKEN.some((x) => x.wert === w.staerke) ? (w.staerke as StrichStaerke) : vorgabe.staerke,
      farbe: FARBEN.some((x) => x.wert === w.farbe) ? (w.farbe as StrichFarbe) : vorgabe.farbe,
    };
  } catch {
    return vorgabe;
  }
}

function wahlGemerkt(): { staerke: StrichStaerke; farbe: StrichFarbe } {
  try {
    return wahlLesen(window.localStorage.getItem(WAHL_SCHLUESSEL));
  } catch {
    return wahlLesen(null);
  }
}

function wahlMerken(w: { staerke: StrichStaerke; farbe: StrichFarbe }): void {
  try {
    window.localStorage.setItem(WAHL_SCHLUESSEL, JSON.stringify(w));
  } catch {
    // Privater Modus oder voll: dann gilt die Wahl nur, solange die Seite offen ist.
  }
}

/** Ein Punkt auf dem Foto: Anteil von Breite und Höhe, auf 4 Stellen – genau genug, und die Daten bleiben klein. */
function anteil(n: number): number {
  return Math.round(Math.min(1, Math.max(0, n)) * 10_000) / 10_000;
}

/** Ab so vielen Bildschirm-Pixeln Bewegung kommt beim Stift ein Punkt dazu. */
const STIFT_SCHRITT = 2;
/** Kleiner als so viele Pixel zählt ein Kreis oder Pfeil als „nur getippt“. */
const KLEINSTE_FORM = 8;

function ZeichnenInhalt({ ich, foto, nr, zumFoto }: { ich: IchAntwort; foto: Foto; nr: number; zumFoto: string }) {
  const url = useDateiAdresse(foto);
  const [striche, setStriche] = useState<Strich[]>(() => foto.zeichnung?.striche ?? []);
  const [werkzeug, setWerkzeug] = useState<ZeichenWerkzeug>("stift");
  const [wahl, setWahl] = useState(wahlGemerkt);
  const [entwurf, setEntwurf] = useState<Strich | null>(null);
  const [tipp, setTipp] = useState(true);
  // Der Zug, der gerade läuft: welcher Finger (oder Stift), der Strich bisher, wo zuletzt ein Punkt dazukam.
  const zug = useRef<{ zeiger: number; art: string; strich: Strich; x: number; y: number } | null>(null);
  // Gilt, solange dieser Zeichenbildschirm offen ist.
  const sperre = useRef<Handballensperre | null>(null);
  const speichern = useSpeichern((s: Strich[]) => zeichnungSpeichern(foto, { fassung: 1, striche: s }), 800);

  const punkteBisher = striche.reduce((summe, s) => summe + s.punkte.length, 0);

  function punkt(e: ReactPointerEvent<HTMLDivElement>): [number, number] {
    const r = e.currentTarget.getBoundingClientRect();
    return [anteil((e.clientX - r.left) / r.width), anteil((e.clientY - r.top) / r.height)];
  }

  function aendern(neu: Strich[]) {
    setStriche(neu);
    speichern.spaeter(neu);
  }

  function beginnen(e: ReactPointerEvent<HTMLDivElement>) {
    // Nach dem ersten Stift zählt der Finger nicht mehr: Es ist der Handballen.
    sperre.current ??= new Handballensperre();
    if (!sperre.current.zaehlt(e.pointerType)) return;
    // Der Stift hat Vorrang: Hat die Hand gerade vor ihm einen Strich begonnen, fällt der weg.
    if (zug.current && e.pointerType === "pen" && zug.current.art === "touch") {
      zug.current = null;
      setEntwurf(null);
    }
    // Ein zweiter Finger zeichnet nicht mit; mit der Maus nur die linke Taste.
    if (zug.current || (e.pointerType === "mouse" && e.button !== 0)) return;
    if (striche.length >= AUFNAHME_GRENZEN.striche || punkteBisher >= AUFNAHME_GRENZEN.punkte) {
      toastZeigen("Mehr geht auf einem Foto nicht – „Zurück“ nimmt den letzten Strich weg.", "fehler");
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = punkt(e);
    const strich: Strich = { werkzeug, staerke: wahl.staerke, farbe: wahl.farbe, punkte: werkzeug === "stift" ? [p] : [p, p] };
    zug.current = { zeiger: e.pointerId, art: e.pointerType, strich, x: e.clientX, y: e.clientY };
    setEntwurf(strich);
    setTipp(false);
  }

  function ziehen(e: ReactPointerEvent<HTMLDivElement>) {
    const z = zug.current;
    if (!z || e.pointerId !== z.zeiger) return;
    if (z.strich.werkzeug === "stift") {
      if (Math.hypot(e.clientX - z.x, e.clientY - z.y) < STIFT_SCHRITT) return;
      if (punkteBisher + z.strich.punkte.length >= AUFNAHME_GRENZEN.punkte) return;
      z.strich = { ...z.strich, punkte: [...z.strich.punkte, punkt(e)] };
      z.x = e.clientX;
      z.y = e.clientY;
    } else {
      z.strich = { ...z.strich, punkte: [z.strich.punkte[0]!, punkt(e)] };
    }
    setEntwurf(z.strich);
  }

  function beenden(e: ReactPointerEvent<HTMLDivElement>) {
    const z = zug.current;
    if (!z || e.pointerId !== z.zeiger) return;
    zug.current = null;
    setEntwurf(null);
    const a = z.strich.punkte[0]!;
    const b = z.strich.punkte[z.strich.punkte.length - 1]!;
    const r = e.currentTarget.getBoundingClientRect();
    // Ein Kreis oder Pfeil ohne Größe war nur ein Tippen – er zählt nicht.
    if (z.strich.werkzeug !== "stift" && Math.hypot((b[0] - a[0]) * r.width, (b[1] - a[1]) * r.height) < KLEINSTE_FORM) return;
    aendern([...striche, z.strich]);
  }

  /** Das System hat den Finger übernommen (etwa eine Geste am Rand) – dieser Strich fällt weg. */
  function abbrechen(e: ReactPointerEvent<HTMLDivElement>) {
    if (zug.current?.zeiger !== e.pointerId) return;
    zug.current = null;
    setEntwurf(null);
  }

  const anzeige: Zeichnung = { fassung: 1, striche: entwurf ? [...striche, entwurf] : striche };
  const tippText = WERKZEUGE.find((w) => w.wert === werkzeug)?.tipp;

  return (
    <div className="zeichnen-seite">
      <AppKopf umgebung={ich.umgebung} zurueck={{ adresse: zumFoto, name: "Foto" }} titel={`Zeichnen · Foto ${nr}`} />
      <div className="zeichen-buehne">
        <div
          className="zeichen-bild"
          style={seitenverhaeltnis(foto)}
          onPointerDown={beginnen}
          onPointerMove={ziehen}
          onPointerUp={beenden}
          onPointerCancel={abbrechen}
        >
          {url && <img src={url} alt={`Foto ${nr}`} draggable={false} />}
          <ZeichenEbene zeichnung={anzeige} breite={foto.breite} hoehe={foto.hoehe} />
        </div>
        {tipp && (
          <span className="chip chip-dunkel zeichen-tipp">
            <Ikon name={WERKZEUGE.find((w) => w.wert === werkzeug)?.ikon ?? "stift"} groesse="k" /> {tippText}
          </span>
        )}
      </div>
      <div className="zeichen-wahl">
        <div className="zeichen-wahl-gruppe" role="radiogroup" aria-label="Strichstärke">
          {STAERKEN.map((st) => (
            <button
              key={st.wert}
              type="button"
              role="radio"
              aria-checked={wahl.staerke === st.wert}
              aria-label={`Strich ${st.name}`}
              title={`Strich ${st.name}`}
              className={`zeichen-staerke staerke-${st.wert}${wahl.staerke === st.wert ? " an" : ""}`}
              onClick={() => {
                const neu = { ...wahl, staerke: st.wert };
                setWahl(neu);
                wahlMerken(neu);
              }}
            >
              <i style={{ background: STRICH_FARBWERTE[wahl.farbe] }} />
            </button>
          ))}
        </div>
        <div className="zeichen-wahl-gruppe" role="radiogroup" aria-label="Farbe">
          {FARBEN.map((f) => (
            <button
              key={f.wert}
              type="button"
              role="radio"
              aria-checked={wahl.farbe === f.wert}
              aria-label={`Farbe ${f.name}`}
              title={f.name}
              className={`zeichen-farbe${wahl.farbe === f.wert ? " an" : ""}`}
              onClick={() => {
                const neu = { ...wahl, farbe: f.wert };
                setWahl(neu);
                wahlMerken(neu);
              }}
            >
              <i style={{ background: STRICH_FARBWERTE[f.wert] }} />
            </button>
          ))}
        </div>
      </div>
      <div className="werkzeuge" role="toolbar" aria-label="Werkzeuge">
        {WERKZEUGE.map((w) => (
          <button
            key={w.wert}
            type="button"
            className={`werkzeug${werkzeug === w.wert ? " an" : ""}`}
            aria-pressed={werkzeug === w.wert}
            onClick={() => {
              setWerkzeug(w.wert);
              setTipp(true);
            }}
          >
            <Ikon name={w.ikon} />
            <span>{w.name}</span>
          </button>
        ))}
        <button type="button" className="werkzeug" disabled={striche.length === 0} onClick={() => aendern(striche.slice(0, -1))}>
          <Ikon name="zurueck" />
          <span>Zurück</span>
        </button>
        <a href={zumFoto} className="werkzeug werkzeug-fertig" onClick={speichern.sofort}>
          <Ikon name="haken" />
          <span>Fertig</span>
        </a>
      </div>
    </div>
  );
}

/**
 * Gesendet (seit Schritt 4): Zeichnen geht nicht mehr – das sagt die Seite
 * und führt zurück zum Foto. Im Nachtrag (seit Schritt 5) nur auf Fotos,
 * die mit ihm dazukamen.
 */
function SchonGesendetHinweis({ ich, zumFoto, imNachtrag }: { ich: IchAntwort; zumFoto: string; imNachtrag: boolean }) {
  return (
    <div className="app-seite">
      <AppKopf umgebung={ich.umgebung} zurueck={{ adresse: zumFoto, name: "Foto" }} titel="Zeichnen" gross="Schon gesendet" />
      <main className="app-inhalt">
        <Tipp art="grau">
          {imNachtrag
            ? "Dieses Foto ist schon im Büro – es bleibt, wie es ist. Zeichnen geht auf Fotos, die du jetzt nachreichst."
            : "Diese Besichtigung ist schon beim Büro – auf ihren Fotos lässt sich nicht mehr zeichnen. Neue Fotos kommen mit „Etwas nachreichen“ dazu."}
        </Tipp>
        <a href={zumFoto} className="knopf-rand knopf-block">
          <Ikon name="links" /> Zurück zum Foto
        </a>
      </main>
    </div>
  );
}

export function ZeichnenSeite({ ich, aufnahmeId, nr, fotoId }: { ich: IchAntwort; aufnahmeId: string; nr: number; fotoId: string }) {
  const daten = useFotoDaten(aufnahmeId, nr, fotoId);
  if (daten === undefined) return <div className="zeichnen-seite" />;
  if (daten === null) return <FotoFehlt ich={ich} aufnahmeId={aufnahmeId} nr={nr} />;
  const { ganz, station, fotos, foto } = daten;
  const modus = modusVon(ganz);
  if (!fotoBearbeitbar(foto, modus)) {
    return <SchonGesendetHinweis ich={ich} zumFoto={fotoAdresse(ganz.aufnahme.id, station.nr, foto.id)} imNachtrag={modus.art === "nachtrag"} />;
  }
  return (
    <ZeichnenInhalt
      key={foto.id}
      ich={ich}
      foto={foto}
      nr={fotos.indexOf(foto) + 1}
      zumFoto={fotoAdresse(ganz.aufnahme.id, station.nr, foto.id)}
    />
  );
}
